import type {
    AllCoverablesDTO,
    CoverableDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote/';
import CompletePurchaseStep from '../steps/CompletePurchaseStep';
import CustomerDetailsStep from '../steps/CustomerDetailsStep';
import DatesAndCancellationStep from '../steps/DatesAndCancellationStep';
import PaymentInformationStep from '../steps/PaymentInformationStep';
import { MissingInformationStep } from '../steps/MissingInformationStep';
import { doesAnyCoverableHaveMissingInformation } from './CheckMissingCoverableConfig';
import type { ExternaleCaseDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/policyjob/edi/dto/';
import {
    isUnsupportedByEdi,
    allCoverablesToCoverableDTOArray
} from '../../../utils/coverablesUtil';
import { filterAccEDICasesForOtherPerson } from '../../../utils/externalCasesUtil';
import _ from 'lodash';

enum ACCORDION_STEPS {
    CUSTOMER_DETAILS = 'CustomerDetailsStep',
    MISSING_INFORMATION = 'MissingInformationStep',
    DATES_AND_CANCELLATION = 'DatesAndCancellationStep',
    PAYMENT_INFORMATION = 'PaymentInformationStep',
    COMPLETE_PURCHASE = 'CompletePurchaseStep'
}

interface CheckoutAccordionConfig {
    stepId: ACCORDION_STEPS;
    titleKey: string;
    disabled: boolean;
    Component: (props: { goNext: () => void; fixedId?: string }) => React.JSX.Element;
}

type status = 'Added' | 'Changed' | 'Removed';

const containsOnlyCoverablesWithStatus = (
    coverables: AllCoverablesDTO,
    status: status
): boolean => {
    const coverablesList = listWithCoverables(coverables);

    if (coverablesList.length === 0) return false;

    return coverablesList.every((coverables) =>
        coverables.every((coverable: CoverableDTO) => coverable.status === status)
    );
};

const allCoverablesHasEqualStatus = (coverables: AllCoverablesDTO): boolean => {
    const coverablesList = listWithCoverables(coverables);

    if (coverablesList.length === 0) return false;

    return coverablesList.every((coverables) =>
        coverables.every((val: CoverableDTO) => val.status === coverablesList[0][0].status)
    );
};

const containsOnlyAccidentSpinOff = (coverables: AllCoverablesDTO): boolean => {
    const coverablesList = listWithCoverables(coverables);

    if (coverablesList.length === 0) return false;

    return coverablesList.every((coverables) =>
        coverables.every(
            (val: CoverableDTO) =>
                val.willSpinOff &&
                (val.flavourName === 'Ulykkesforsikring' ||
                    val.flavourName === 'Ulykkesforsikring til unge')
        )
    );
};

const listWithCoverables = (coverables: AllCoverablesDTO): any[] => {
    return Object.values(coverables).filter((value) => Object.keys(value).length !== 0);
};

const doesNotContainAnyCoverablesWithStatus = (
    coverables: AllCoverablesDTO,
    status: status
): boolean => {
    const coverablesList = listWithCoverables(coverables);

    if (coverablesList.length === 0) return true;

    return !coverablesList.some((coverables) =>
        coverables.some((coverable: CoverableDTO) => coverable.status === status)
    );
};

const isTheAccordionTheLastStep = (
    coverables: AllCoverablesDTO,
    externalCases: ExternaleCaseDTO[],
    isPolicyChange: boolean,
    accordionStepId: ACCORDION_STEPS,
    isCVR?: boolean,
    containsAddCoverageChange?: boolean,
    notUpdatedCoverables?: AllCoverablesDTO
): boolean => {
    const activeAccordions = getCheckoutConfig(
        coverables,
        externalCases,
        isPolicyChange,
        isCVR,
        containsAddCoverageChange,
        notUpdatedCoverables
    ).filter((a) => !a.disabled);
    const accordion = activeAccordions.find((e) => e.stepId === accordionStepId);
    return (
        (accordion && activeAccordions.indexOf(accordion) === activeAccordions.length - 1) ?? false
    );
};

const getCheckoutConfig = (
    coverables: AllCoverablesDTO,
    externalCases: ExternaleCaseDTO[],
    isPolicyChange: boolean,
    isCVR?: boolean,
    containsAddCoverageChange?: boolean,
    notUpdatedCoverables?: AllCoverablesDTO
): CheckoutAccordionConfig[] => {
    const allCoverablesEqualStatus = allCoverablesHasEqualStatus(coverables);
    const containsOnlyStatusAdded = containsOnlyCoverablesWithStatus(coverables, 'Added');
    const coverablesHasAllInfo = !doesAnyCoverableHaveMissingInformation(
        notUpdatedCoverables ?? coverables
    );
    const noExternalCases = _.isEmpty(
        filterAccEDICasesForOtherPerson(externalCases, allCoverablesToCoverableDTOArray(coverables))
    );
    const containsOnlyAccSpinOff = containsOnlyAccidentSpinOff(coverables);
    const coverablesList = listWithCoverables(coverables);
    const listLobsUnsupportedByEdi = coverablesList.flat().filter((cov) => isUnsupportedByEdi(cov));
    const noCoverableWithAddedStatus = doesNotContainAnyCoverablesWithStatus(coverables, 'Added');

    return [
        {
            stepId: ACCORDION_STEPS.CUSTOMER_DETAILS,
            titleKey: isCVR
                ? 'DSS-CONTACT-INFORMATION-CVR-HEADER'
                : 'DSS-CONTACT-INFORMATION-HEADER',
            Component: CustomerDetailsStep,
            disabled: (isPolicyChange && allCoverablesEqualStatus) || containsOnlyAccSpinOff
        },
        {
            stepId: ACCORDION_STEPS.MISSING_INFORMATION,
            titleKey: 'DSS-ADDITIONAL-INFORMATION-HEADER',
            Component: MissingInformationStep,
            disabled: coverablesHasAllInfo || (isPolicyChange && noCoverableWithAddedStatus)
        },
        {
            stepId: ACCORDION_STEPS.DATES_AND_CANCELLATION,
            titleKey: 'DSS-EFFECTIVE-DATE-AND-CANCELLATION-STARTDATE-ACCORDION-HEADER',
            Component: DatesAndCancellationStep,
            disabled:
                (!isPolicyChange && noExternalCases && listLobsUnsupportedByEdi.length === 0) ||
                // eslint-disable-next-line prettier/prettier
                (isPolicyChange &&
                    noExternalCases &&
                    allCoverablesEqualStatus &&
                    !containsOnlyStatusAdded) ||
                (noExternalCases && containsOnlyAccSpinOff)
        },
        {
            stepId: ACCORDION_STEPS.PAYMENT_INFORMATION,
            titleKey: 'DSS-PAYMENT-INFO-SCREEN-HEADER',
            Component: PaymentInformationStep,
            disabled: isPolicyChange || containsOnlyAccSpinOff
        },
        {
            stepId: ACCORDION_STEPS.COMPLETE_PURCHASE,
            titleKey: 'DSS-CHECKOUT-SCREEN-HEADER',
            Component: CompletePurchaseStep,
            disabled:
                (isPolicyChange &&
                    allCoverablesEqualStatus &&
                    !containsOnlyStatusAdded &&
                    !containsAddCoverageChange) ||
                containsOnlyAccSpinOff
        }
    ];
};

export {
    getCheckoutConfig,
    containsOnlyAccidentSpinOff,
    type CheckoutAccordionConfig,
    isTheAccordionTheLastStep,
    ACCORDION_STEPS
};
